import {

  BookingSummary,

  PaymentStatus,

  PaymentSummary,

} from '@/services/BookingService.interface'



export type ReserveActionKind = 'options' | 'payment' | 'balance'



export interface ReserveAction {

  id: string

  /** Full text for single-action button + screen readers */

  label: string

  title: string

  subtitle?: string

  /** Formatted amount shown large in the modal (payment rows). */
  amount?: string

  url: string

  kind: ReserveActionKind

  instalmentNumber?: number

}



const TERMINAL_STATUSES = new Set([

  'Annulé',

  'Remboursé',

  'Refusé',

  'Échoué',

  'Cancelled',

  'Refunded',

  'Refused',

  'Failed',

])



const PAYMENT_LINK_ACTION = 'Payment Link'



function normalizePaymentStatus(status?: string): string {

  const value = String(status ?? '').trim()

  const mapping: Record<string, string> = {

    Scheduled: 'Echéance',

    Paid: PaymentStatus.PAID,

    Cancelled: 'Annulé',

    Refunded: 'Remboursé',

    Refused: 'Refusé',

    Failed: 'Échoué',

    'Payment Link': 'Payment Link',

    NOT_PAID: 'NOT_PAID',

    'Not paid': 'NOT_PAID',
    'not paid': 'NOT_PAID',
    not_paid: 'NOT_PAID',
  }

  return mapping[value] || value

}



function getPaymentAction(payment: PaymentSummary): string {
  const row = payment as PaymentSummary & { paymentAction?: string | null }
  return String(row.payment_action ?? row.paymentAction ?? '').trim()
}

function isPaymentLinkActionRaw(action: string): boolean {
  const normalized = action.toUpperCase().replace(/\s+/g, ' ')
  return normalized === 'PAYMENT LINK' || normalized === PAYMENT_LINK_ACTION.toUpperCase()
}

/** Cart/quote BO row: `payment_action` = Payment Link (or legacy status = Payment Link). */
export function isPaymentLinkLine(payment: PaymentSummary): boolean {
  if (isPaymentLinkActionRaw(getPaymentAction(payment))) return true
  return normalizePaymentStatus(payment.status) === 'Payment Link'
}

export function isPaymentPaid(payment: PaymentSummary): boolean {
  return normalizePaymentStatus(payment.status) === PaymentStatus.PAID
}

/** Unpaid payment-link row (aligned with call-center `isBoUnpaidInstalmentLine`). */
export function isUnpaidPaymentLink(payment: PaymentSummary): boolean {
  if (isPaymentPaid(payment)) return false
  const status = normalizePaymentStatus(payment.status)
  if (TERMINAL_STATUSES.has(status)) return false
  return isPaymentLinkLine(payment)
}

/** Scheduled instalment (échéance / Scheduled) — not a payment-link row. */
export function isScheduledEcheancePayment(payment: PaymentSummary): boolean {
  if (isPaymentPaid(payment)) return false
  if (isPaymentLinkLine(payment)) return false
  const status = normalizePaymentStatus(payment.status)
  if (TERMINAL_STATUSES.has(status)) return false
  return status === 'Echéance'
}

/** Only unpaid payment links or scheduled échéances — never `leftToPay` alone. */
export function isBalancePagePayablePayment(payment: PaymentSummary): boolean {
  return isUnpaidPaymentLink(payment) || isScheduledEcheancePayment(payment)
}



export function bookingHasPaidPayment(booking: BookingSummary): boolean {

  return (booking.payments ?? []).some(isPaymentPaid)

}



export function getPayablePayments(booking: BookingSummary): PaymentSummary[] {

  return (booking.payments ?? []).filter(isBalancePagePayablePayment)

}



/** Quote/cart with no payment rows on the dossier. */
export function bookingHasNoPayments(booking: BookingSummary): boolean {
  return (booking.payments ?? []).length === 0
}

/** Single Réserver click → options only (no modal). */
export function canShowReserveToOptions(booking: BookingSummary): boolean {
  return bookingHasNoPayments(booking) && Boolean(booking.quoteLink?.trim())
}

/**
 * Réserver visible while nothing is paid yet: options only, or options + unpaid links in a modal.
 */
export function canShowReserveButton(booking: BookingSummary): boolean {
  if (bookingHasPaidPayment(booking)) return false
  if (canShowReserveToOptions(booking)) return true
  if (!booking.quoteLink?.trim() || !booking.paymentLink?.trim()) return false
  return getPayablePayments(booking).length > 0
}

/** Payer le solde — at least one payment paid and payable lines left. */
export function canShowPayBalanceActions(booking: BookingSummary): boolean {
  if (bookingHasNoPayments(booking)) return false
  if (!bookingHasPaidPayment(booking)) return false
  if (!booking.paymentLink?.trim()) return false
  return getPayablePayments(booking).length > 0
}

/** Unpaid payment-link rows while deposit is still outstanding (cart/quote deposit collection). */
export function getUnpaidDepositPaymentLinks(
  booking: BookingSummary
): PaymentSummary[] {
  if (booking.depositMet) return []
  return (booking.payments ?? []).filter(isUnpaidPaymentLink)
}

export function hasPayableDepositPaymentLine(booking: BookingSummary): boolean {
  return getUnpaidDepositPaymentLinks(booking).length > 0
}

/** Deposit to display: remaining cents if partly paid, else `initialPaymentRequired` (cents). */
export function getDepositDisplayAmountCents(
  booking: BookingSummary
): number | null {
  const remaining = booking.depositRemainingCents
  if (remaining != null && Number.isFinite(Number(remaining))) {
    const cents = Math.round(Number(remaining))
    if (cents > 0) return cents
  }
  const initial = booking.initialPaymentRequired
  if (initial != null && Number.isFinite(Number(initial))) {
    const cents = Math.round(Number(initial))
    if (cents > 0) return cents
  }
  return null
}

function buildOptionsReserveAction(
  booking: BookingSummary,
  labels: { reserve: string; payDeposit: string },
  formatAmount: (amount: string | number) => string,
  buildAction: (
    base: Omit<ReserveAction, 'label'>,
    label: string
  ) => ReserveAction,
  /** Modal with payment links: show deposit label + amount. */
  showDepositInModal: boolean
): ReserveAction {
  const depositCents = getDepositDisplayAmountCents(booking)
  const depositAmount =
    depositCents != null ? formatAmount(depositCents) : undefined

  if (showDepositInModal) {
    return buildAction(
      {
        id: 'options',
        title: labels.payDeposit,
        amount: depositAmount,
        url: booking.quoteLink!,
        kind: 'options',
      },
      labels.payDeposit
    )
  }

  return buildAction(
    {
      id: 'options',
      title: labels.reserve,
      url: booking.quoteLink!,
      kind: 'options',
    },
    labels.reserve
  )
}

export function buildBalancePaymentUrl(

  paymentLink: string,

  paymentId?: string

): string {

  const url = new URL(paymentLink)

  if (paymentId) {

    url.searchParams.set('paymentId', paymentId)

  }

  return url.toString()

}



export function getReserveActions(

  booking: BookingSummary,

  labels: {

    reserve: string

    payDeposit: string

    payBalance: string

    depositHint: string

    paymentLinkTitle: (index: number) => string
    paymentLinkTitleSingle: string
    echeanceTitle: (index: number) => string
    echeanceTitleSingle: string
    dueBy: (date: string) => string

    payPaymentLink: (amount: string) => string

    payPaymentLinkWithDate: (amount: string, date: string) => string

    payPaymentLinkInstalment: (instalment: number, amount: string) => string

    payPaymentLinkInstalmentWithDate: (

      instalment: number,

      amount: string,

      date: string

    ) => string

  },

  formatAmount: (amount: string | number) => string

): ReserveAction[] {

  const buildAction = (

    base: Omit<ReserveAction, 'label'>,

    label: string

  ): ReserveAction => ({ ...base, label })

  const actions: ReserveAction[] = []

  const payable = getPayablePayments(booking)

  const paymentLink = booking.paymentLink?.trim()

  const appendPayablePaymentActions = (): void => {
    if (!paymentLink || payable.length === 0) return

    const sortedPayable = [...payable].sort((a, b) => {
      const dateA = a.datePayment || ''
      const dateB = b.datePayment || ''
      if (dateA !== dateB) return dateA.localeCompare(dateB)
      return a.id.localeCompare(b.id)
    })

    const labelInstalments = sortedPayable.length > 1

    sortedPayable.forEach((payment, index) => {
      const amountLabel = formatAmount(payment.amount)
      const instalmentNumber = index + 1
      const isLink = isUnpaidPaymentLink(payment)

      const title = labelInstalments
        ? isLink
          ? labels.paymentLinkTitle(instalmentNumber)
          : labels.echeanceTitle(instalmentNumber)
        : isLink
          ? labels.paymentLinkTitleSingle
          : labels.echeanceTitleSingle

      const isDepositLink = isLink && !booking.depositMet
      const label = labelInstalments
        ? labels.payPaymentLinkInstalment(instalmentNumber, amountLabel)
        : isDepositLink
          ? `${labels.payDeposit} ${amountLabel}`
          : isLink
            ? labels.payPaymentLink(amountLabel)
            : `${labels.payBalance} ${amountLabel}`

      actions.push(
        buildAction(
          {
            id: `payment-${payment.id}`,
            title,
            amount: amountLabel,
            url: buildBalancePaymentUrl(paymentLink, payment.id),
            kind: 'payment',
            instalmentNumber: labelInstalments ? instalmentNumber : undefined,
          },
          label
        )
      )
    })

    if (bookingHasPaidPayment(booking) && payable.length > 1) {
      actions.push(
        buildAction(
          {
            id: 'balance',
            title: labels.payBalance,
            url: paymentLink,
            kind: 'balance',
          },
          labels.payBalance
        )
      )
    }
  }

  if (canShowReserveToOptions(booking)) {
    return [
      buildOptionsReserveAction(
        booking,
        labels,
        formatAmount,
        buildAction,
        false
      ),
    ]
  }

  if (canShowPayBalanceActions(booking)) {
    appendPayablePaymentActions()
    return actions
  }

  if (canShowReserveButton(booking) && booking.quoteLink) {
    actions.push(
      buildOptionsReserveAction(
        booking,
        labels,
        formatAmount,
        buildAction,
        true
      )
    )
    appendPayablePaymentActions()
    return actions
  }

  return actions
}

export type ReserveActionLabels = Parameters<typeof getReserveActions>[1]

/**
 * CTA label for the main pay / reserve button.
 * Uses real payable payment rows — never `depositRemainingCents` unless a matching line exists.
 */
export function getPayActionLabel(
  booking: BookingSummary,
  labels: ReserveActionLabels,
  formatAmount: (amount: string | number) => string
): string {
  const actions = getReserveActions(booking, labels, formatAmount)

  if (actions.length > 1) {
    return labels.payBalance
  }

  if (actions.length === 1) {
    const action = actions[0]
    if (action.kind === 'options') {
      const depositCents = getDepositDisplayAmountCents(booking)
      if (depositCents != null) {
        const formatted = formatAmount(depositCents)
        if (formatted) return `${labels.payDeposit} ${formatted}`
      }
    }
    return action.label
  }

  const left = booking.leftToPay
  if (left != null && Number(left) > 0) {
    const formatted = formatAmount(left)
    return formatted ? `${labels.payBalance} ${formatted}` : labels.payBalance
  }

  return labels.payBalance
}


